import { CAT_NAMES } from './catalogI18n';
import { SECTOR_ITEMS, SECTOR_NAMES, GARDEN_PRICES } from './catalogSectors';

export const CAT_KEY = 'giss_catalog';
export const SECTORS = ['water', 'sewer', 'power', 'building', 'garden', 'survey', 'gas', 'inhouse', 'roof', 'heat'];
export const CATS = ['valve', 'hydrant', 'house', 'dma', 'pipe', 'repair', 'shaft', 'sewer', 'gas', 'cable', 'fiber', 'facade', 'scaffold', 'garden', 'svpt', 'topo', 'inhouse', 'roof', 'heat'];
// media (Sparten) of water / sewer networks; fittings store the chosen one as f.med
export const MEDS = ['water', 'gas', 'power', 'telecom', 'heat', 'sewer', 'rain', 'fire', 'tank'];
export const MED_COLOR = { water: '#2563eb', gas: '#e6b800', power: '#ef4444', telecom: '#8b5cf6', heat: '#be123c', sewer: '#8b5a2b', rain: '#0891b2', fire: '#dc2626', tank: '#7c3aed' };
const MED_ID = { f04: ['fire', 'tank'], f05: ['fire', 'tank'], t02: ['tank', 'rain'], t07: ['tank', 'rain'] };
export function medsOf(x) {
  if (x.meds) return x.meds;
  const id = x.id || '', p = id[0];
  if (MED_ID[id]) return MED_ID[id];
  if (p === 'f') return ['fire'];
  if (p === 'r') return ['rain'];
  if (p === 'h') return ['heat'];
  if (p === 'p') return [x.cat === 'gas' ? 'gas' : x.cat === 'fiber' || x.type === 'telecom' ? 'telecom' : 'power'];
  if (p === 't') return ['tank'];
  if (p === 'x') return x.cat === 'house' ? ['water'] : x.kind === 'pipe' ? ['water', 'fire'] : ['water', 'fire', 'tank'];
  if (p === 'k') return ['sewer', 'rain'];
  if (p === 's' && ['shaft', 'sewer', 'pipe'].includes(x.cat)) return ['sewer', 'rain'];
  if (p === 'w') {
    if (x.cat === 'house' || x.cat === 'pipe') return ['water'];
    if (['valve', 'hydrant', 'dma', 'repair', 'shaft'].includes(x.cat)) return ['water', 'fire', 'tank'];
  }
  return null;
}
export const UNITS = ['pcs', 'lfm', 'm2', 'm3', 'l', 'kg', 'lump'];
export const DN_PIPES = [25, 32, 40, 50, 63, 80, 100, 125, 150, 200, 250, 300, 350, 400, 450, 500, 600, 700, 800, 900, 1000, 1200];
export const nearDn = (mm) => DN_PIPES.reduce((b, d) => (Math.abs(d - mm) < Math.abs(b - mm) ? d : b), DN_PIPES[0]);
export const D_GARDEN = [16, 20, 25, 32, 40, 50, 63];
// garden/agri/forestry branch: only garden items (+ own items without sector)
export const gardenItems = (list) => list.filter((x) => ['garden', 'all'].includes(secOf(x)));

const C = { valve: ['#1d6fe8', 'valve'], hydrant: ['#d62828', 'hydrant'], house: ['#15803d', 'house'], dma: ['#0d9488', 'other'], repair: ['#ea580c', 'other'], shaft: ['#4b5563', 'manhole'], cable: ['#ef4444', 'cjoint'] };
const pt = (cat, name, sym, dim = '', extra = {}) => ({ cat, name, sym, dim, unit: 'pcs', kind: 'point', color: C[cat][0], type: C[cat][1], ...extra });
const pipe = (name, color, dim, cat = 'pipe') => ({ cat, name, sym: '', dim, unit: 'lfm', kind: 'pipe', color, type: 'water' });
const cable = (name, type, sub, dim) => ({ cat: 'cable', name, sym: '', dim, unit: 'lfm', kind: 'pipe', color: type === 'power' ? '#ef4444' : '#8b5cf6', type, sub });

const WATER = [
  pt('valve', 'Streckenschieber (Hauptleitungsschieber)', 'SS', 'DN 100'), pt('valve', 'Koppelschieber', 'KS', 'DN 100'),
  pt('valve', 'Hausschieber (Hausanschlussschieber)', 'HS', 'DN 40'), pt('valve', 'Absperrklappe', 'AK', 'DN 300'),
  pt('valve', 'Anbohrarmatur mit Schieber', 'AB', 'DN 100 / 1 1/4"'), pt('valve', 'Anbohrschelle ohne Schieber', 'AS', 'DN 100 / 1"'),
  pt('valve', 'Straßenkappe starr', 'SK'), pt('valve', 'Straßenkappe höhenverstellbar', 'SKH'), pt('valve', 'Einbaugarnitur / Teleskopschutzrohr', 'EG', 'RD 1,3–1,8 m'),
  pt('hydrant', 'Unterflurhydrant (UFH)', 'UH', 'DN 80'), pt('hydrant', 'Überflurhydrant (ÜFH)', 'ÜH', 'DN 100'), pt('hydrant', 'Schachthydrant', 'SH', 'DN 80'),
  pt('hydrant', 'Be- und Entlüftungsventil (BEV)', 'BE', 'DN 50', { color: '#0891b2', type: 'air' }),
  pt('hydrant', 'Entleerungsschieber / Spülstutzen', 'EL', 'DN 80', { color: '#7c3aed', type: 'drain' }),
  pipe('Hausanschlussleitung (HA-Leitung)', '#16a34a', 'PE 100 RC d40', 'house'),
  pt('house', 'Hauptabsperreinrichtung (HAE)', 'HAE', '1 1/4"'), pt('house', 'Hauswasserzähler', 'WZ', 'Q3=4'),
  pt('house', 'Großwasserzähler / Verbundzähler', 'GZ', 'DN 50'), pt('house', 'Bauwasserzähler', 'BZ', 'Q3=10'),
  pt('house', 'Wasserzählerbügel / Zähleranlage', 'ZB', '1"'), pt('house', 'Rückflussverhinderer (RV)', 'RV', '1"'), pt('house', 'Druckminderer', 'DM', '1"'),
  pt('dma', 'Zonenzähler / DMA-Messschacht', 'DMA', 'DN 150'), pt('dma', 'Druckregelventil (PRV)', 'PRV', 'DN 100'),
  pt('dma', 'Durchflussmesser (MID)', 'MID', 'DN 150'), pt('dma', 'Datenlogger / Akustik-Logger', 'LOG'), pt('dma', 'Messpunkt / Kontaktpunkt', 'MP'),
  pipe('Trinkwasserleitung Guss (GGG)', '#1d4ed8', 'GGG 40'), pipe('Trinkwasserleitung PE-HD / PE 100 RC', '#0ea5e9', 'PE 100 RC SDR 11'),
  pipe('Trinkwasserleitung PVC', '#6366f1', 'PVC-U PN 10'), pipe('Trinkwasserleitung Stahl', '#475569', 'St'), pipe('Trinkwasserleitung Faserzement', '#a16207', 'FZ'),
  pt('repair', 'Reparaturschelle / Dichtschelle', 'RS', 'DN 100'), pt('repair', 'Kupplung / Flanschadapter', 'KU', 'DN 100'),
  pt('repair', 'Überschieber', 'ÜS', 'DN 100'), pt('repair', 'Bogen', 'BO', 'DN 100 / 30°'), pt('repair', 'T-Stück', 'T', 'DN 100/80'), pt('repair', 'Reduzierstück', 'RE', 'DN 150/100'),
  pt('shaft', 'Schacht (Armaturen- / Messschacht)', 'SC', 'DN 1000'),
  cable('Stromkabel Niederspannung (NS 0,4 kV)', 'power', 'ns', 'NAYY 4x150'), cable('Stromkabel Mittelspannung (MS 10–30 kV)', 'power', 'ms', 'NA2XS(F)2Y 1x150'),
  cable('Stromkabel Hochspannung (HS)', 'power', 'hs', '110 kV'), cable('Kabel Straßenbeleuchtung', 'power', 'light', 'NYY-J 5x2,5'), cable('Erdungskabel / Erder', 'power', 'earth', 'Cu 50 mm²'),
  cable('Glasfaserkabel (LWL / FTTH)', 'telecom', 'fiber', '24 Fasern'), cable('Fernmeldekabel / Kupfer', 'telecom', 'copper', 'A-2Y(L)2Y'),
  cable('Steuer- & Signalkabel', 'telecom', 'signal', 'J-Y(St)Y'), cable('Kabelschutzrohr / Leerrohr', 'telecom', 'duct', 'PE-HD d50'),
  pt('cable', 'Kabelmuffe', 'KM'), pt('cable', 'Kabelschacht', 'KSC', '', { color: '#4b5563', type: 'manhole' }),
  pt('cable', 'Verteilerkasten / KVz', 'KVz', '', { color: '#8b5cf6', type: 'cabinet' }), pt('cable', 'Trassenwarnband', 'TW', '', { color: '#eab308', type: 'other' }),
  pt('valve', 'Schieberkreuz (4-Wege)', 'SK4', 'DN 100', { type: 'cross4' }), pt('valve', 'T-Schieberkreuz / Abzweig (3-Wege)', 'T3', 'DN 100/80', { type: 'tee3' }),
  pt('house', 'Wasserzähler (WZ) im Gebäude', 'WZ', 'Q3=4', { color: '#0891b2', type: 'meter' }),
  // hydraulic objects (EPANET symbols): junction, pump, pressure reducer, elevated tank / supply
  pt('valve', 'Knoten / Verbindungspunkt', 'K', '', { color: '#334155', type: 'node' }), pt('valve', 'Pumpe (Druckerhöhung)', 'P', '', { color: '#0f766e', type: 'pump' }),
  pt('valve', 'Druckminderer', 'DM', 'DN 100', { color: '#9333ea', type: 'prv' }), pt('dma', 'Hochbehälter / Einspeisung', 'HB', '', { color: '#0369a1', type: 'source' }),
];
const CABLE_FROM = WATER.findIndex((x) => x.cat === 'cable' || x.type === 'power');
const V3 = ['cross4', 'tee3', 'meter'];

const wid = (i) => `w${String(i + 1).padStart(2, '0')}`;
const DE_NAMES = { ...Object.fromEntries(WATER.map((x, i) => [wid(i), x.name])), ...Object.fromEntries(SECTOR_ITEMS.map((x) => [x.id, x.name])) };
// factory names follow the app language as long as the user did not rename them
const trName = (id) => { const l = localStorage.getItem('baustelle_app_lang'); return CAT_NAMES[l]?.[id] || SECTOR_NAMES[l]?.[id]; };
const localize = (it) => (trName(it.id) && it.name === DE_NAMES[it.id] ? { ...it, name: trName(it.id) } : it);
const delocalize = (it) => (trName(it.id) && it.name === trName(it.id) ? { ...it, name: DE_NAMES[it.id] } : it);

export const factoryCatalog = () => [...WATER.map((x, i) => ({ id: wid(i), sku: '', price: null, install: null, ...x })), ...SECTOR_ITEMS.map((x) => ({ sku: '', price: null, install: null, ...x }))];
// pipe branch: water, sewer and power parts only
// sector of an item: explicit, cables of the water list -> power, own items -> all sectors
export const secOf = (x) => x.sector || (x.id?.[0] === 'w' ? (x.cat === 'cable' || x.type === 'power' || x.type === 'telecom' ? 'power' : 'water') : 'all');
// plant / operator assets (waterworks, tanks, DMA logging) are not built by a civil-works contractor
const NET_HIDE = new Set(['x06', 'x07', 'x08', 'x09', 'x10', 't01', 't04', 't05', 'w23', 'w24', 'w25', 'w26', 'w27', 'f05', 'p09']);
export const inSector = (x, sec) => !sec || sec === 'all' || (sec === 'net' ? !NET_HIDE.has(x.id) && (secOf(x) === 'all' || secOf(x) === 'net' || ['water', 'sewer', 'power', 'heat', 'gas'].includes(secOf(x))) : secOf(x) === 'all' || secOf(x) === sec || (sec === 'water' && secOf(x) === 'heat'));
export const bySector = (items, sec) => [...items].sort((a, b) => (inSector(b, sec) ? 1 : 0) - (inSector(a, sec) ? 1 : 0));

export function getCatalog() {
  let c = null;
  try { c = JSON.parse(localStorage.getItem(CAT_KEY)); } catch { /* fresh */ }
  if (!c?.items) c = { sector: 'water', items: factoryCatalog(), cables: 1, v3: 1, v4: 1, v5: 1, v6: 1, v7: 1, v8: 1, v9: 1, v10: 1, v11: 1, v12: 1, v13: 1, v14: 1, v15: 1, v16: 1 };
  if (!c.cables) {
    const have = new Set(c.items.map((x) => x.id));
    c = { ...c, cables: 1, items: [...c.items, ...factoryCatalog().slice(CABLE_FROM).filter((x) => !have.has(x.id))] };
    localStorage.setItem(CAT_KEY, JSON.stringify(c));
  }
  if (!c.v3) {
    const have = new Set(c.items.map((x) => x.id));
    c = { ...c, v3: 1, items: [...c.items, ...factoryCatalog().filter((x) => V3.includes(x.type) && !have.has(x.id))] };
    localStorage.setItem(CAT_KEY, JSON.stringify(c));
  }
  if (!c.v8) {
    const have = new Set(c.items.map((x) => x.id));
    c = { ...c, v4: 1, v5: 1, v6: 1, v7: 1, v8: 1, items: [...c.items, ...factoryCatalog().filter((x) => !have.has(x.id))] };
    localStorage.setItem(CAT_KEY, JSON.stringify(c));
  }
  if (!c.v9) {
    const have = new Set(c.items.map((x) => x.id));
    c = { ...c, v9: 1, items: [...c.items, ...factoryCatalog().filter((x) => !have.has(x.id))] };
    localStorage.setItem(CAT_KEY, JSON.stringify(c));
  }
  if (!c.v11) {
    const have = new Set(c.items.map((x) => x.id));
    c = { ...c, v11: 1, items: [...c.items, ...factoryCatalog().filter((x) => !have.has(x.id))] };
    localStorage.setItem(CAT_KEY, JSON.stringify(c));
  }
  if (!c.v13) {
    const have = new Set(c.items.map((x) => x.id));
    c = { ...c, v13: 1, items: [...c.items, ...factoryCatalog().filter((x) => !have.has(x.id))] };
    localStorage.setItem(CAT_KEY, JSON.stringify(c));
  }
  if (!c.v14) {
    const have = new Set(c.items.map((x) => x.id));
    c = { ...c, v14: 1, items: [...c.items, ...factoryCatalog().filter((x) => !have.has(x.id))] };
    localStorage.setItem(CAT_KEY, JSON.stringify(c));
  }
  if (!c.v16) {
    // hydraulic objects for AquaFlow (node, pump, pressure reducer, tank)
    const have = new Set(c.items.map((x) => x.id));
    c = { ...c, v16: 1, items: [...c.items, ...factoryCatalog().filter((x) => !have.has(x.id))] };
    localStorage.setItem(CAT_KEY, JSON.stringify(c));
  }
  if (!c.v15) {
    // flat roof catalogue; roof leak detection used the building catalogue before
    const have = new Set(c.items.map((x) => x.id)), det = localStorage.getItem('giss_profiles')?.includes('"detect"'), sub = localStorage.getItem('giss_detect_sub');
    const sec = det && sub === 'roof' && c.sector === 'building' ? 'roof' : det && sub === 'heat' && c.sector === 'water' ? 'heat' : c.sector;
    c = { ...c, v15: 1, sector: sec, items: [...c.items, ...factoryCatalog().filter((x) => !have.has(x.id))] };
    localStorage.setItem(CAT_KEY, JSON.stringify(c));
  }
  if (!c.v12) {
    c = { ...c, v12: 1, items: c.items.map((x) => (/^p0[1-9]$/.test(x.id) && x.sector === 'power' ? { ...x, sector: 'gas' } : x)) };
    localStorage.setItem(CAT_KEY, JSON.stringify(c));
  }
  if (!c.v10) {
    c = { ...c, v10: 1, items: c.items.map((x) => (x.price == null && GARDEN_PRICES[x.id] != null ? { ...x, price: GARDEN_PRICES[x.id] } : x)) };
    localStorage.setItem(CAT_KEY, JSON.stringify(c));
  }
  return { ...c, items: c.items.map(localize) };
}
export const saveCatalog = (c) => {
  localStorage.setItem(CAT_KEY, JSON.stringify({ ...c, items: c.items.map(delocalize) }));
  window.dispatchEvent(new Event('giss-catalog'));
  return getCatalog();
};
// lookup cached per stored catalogue version (getCatalog parses localStorage on every call)
let catCache = { raw: undefined, byId: null };
export const catItem = (id) => {
  const raw = localStorage.getItem(CAT_KEY);
  if (raw !== catCache.raw || !catCache.byId) catCache = { raw, byId: new Map(getCatalog().items.map((x) => [x.id, x])) };
  return catCache.byId.get(id) || null;
};
// cables / microducts carry no nominal width; PE pipes are sized by outer diameter (d)
export const noDn = (it) => ['cable', 'fiber', 'svpt', 'topo'].includes(it.cat) || /kabel|mikrorohr|microduct|cable/i.test(it.name || '');
export const itemLabel = (it, dn) => {
  const size = dn && !noDn(it) ? (it.sector === 'garden' || /\bPE\b/.test(it.name || '') ? `d${dn}` : `DN ${dn}`) : it.dim;
  return [it.name, size && !(it.name || '').includes(size) ? size : ''].filter(Boolean).join(' · ');
};

export function matchItems(items, q, cat) {
  const s = q.trim().toLowerCase();
  return items.filter((x) => (!cat || x.cat === cat) && (!s || `${x.name} ${x.dim} ${x.sku}`.toLowerCase().includes(s)));
}

export async function importCatalog(file) {
  const data = JSON.parse(await file.text());
  const items = Array.isArray(data) ? data : data.items;
  if (!Array.isArray(items) || !items.every((x) => x.id && x.name && x.cat)) throw new Error('format');
  return saveCatalog({ sector: data.sector || 'water', items });
}
